import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GruposService } from 'src/app/core/services/groups/grupos.service';
import { LoginService } from 'src/app/core/services/login';
import { MODAL_DIALOG_TYPES } from 'src/app/core/utils/modal-dialog-types';
import { ReportingComponent } from '../../../../reporting/reporting.component';
import { DashboardProfesorModalComponent } from '../../dashboard-estudio/dashboard-profesor-modal.component';

export type ProfesoresSortColumn = 'name' | 'surname' | 'email';
export type MandosSortColumn = 'idMando' | 'mac' | 'estado' | 'battery';
type SortDir = 'asc' | 'desc';

@Component({
	selector: 'app-detalle-grupo-estudio',
	templateUrl: './detalle-grupo-estudio.component.html',
	styleUrls: ['./detalle-grupo-estudio.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false
})
export class DetalleGrupoEstudioComponent implements OnInit {

	@Input() group: any;

	activeTab: 'informes' | 'dashboard' | 'profesores' | 'mandos' = 'informes';

	profesores: any[] = [];
	mandos: any[] = [];

	loadingProfesores: boolean = false;
	loadingMandos: boolean = false;

	searchProfesores: string = '';
	searchMandos: string = '';

	profesoresSortColumn: ProfesoresSortColumn | null = null;
	profesoresSortDir: SortDir = 'asc';

	mandosSortColumn: MandosSortColumn | null = null;
	mandosSortDir: SortDir = 'asc';

	constructor(
		public activeModal: NgbActiveModal,
		private gruposService: GruposService,
		private modalService: NgbModal,
		private loginService: LoginService
	) {}

	ngOnInit() {
		this.loadProfesores();
		this.loadMandos();
	}

	closeModal() {
		this.activeModal.close();
	}

	setTab(tab: 'informes' | 'dashboard' | 'profesores' | 'mandos') {
		this.activeTab = tab;
	}

	abrirDashboardProfesor(profesor: any) {
		const idUser = profesor?.idUser ?? profesor?.id_user ?? profesor?.idUserProfesor;
		if (!idUser || !this.group?.idGroup) return;
		const profesorName = `${profesor.firstName || profesor.name || ''} ${profesor.surname || profesor.surName || ''}`.trim() || 'Profesor';

		const modalRef = this.modalService.open(DashboardProfesorModalComponent, {
			scrollable: true,
			windowClass: `${MODAL_DIALOG_TYPES.W90} h-100`,
		});
		modalRef.componentInstance.idGroup = this.group.idGroup;
		modalRef.componentInstance.idUser = idUser;
		modalRef.componentInstance.title = profesorName;
		modalRef.componentInstance.subtitle = this.group.title;
	}

	loadProfesores() {
		this.loadingProfesores = true;
		this.gruposService.getListProfesores(this.group.idGroup).subscribe({
			next: (res) => {
				this.profesores = Array.isArray(res) ? res : (res?.data ?? []);
				this.loadingProfesores = false;
			},
			error: () => {
				this.profesores = [];
				this.loadingProfesores = false;
			}
		});
	}

	loadMandos() {
		this.loadingMandos = true;
		this.gruposService.getMandosAsignados(this.group.idGroup).subscribe({
			next: (res) => {
				this.mandos = res?.data ?? (Array.isArray(res) ? res : []);
				this.loadingMandos = false;
			},
			error: () => {
				this.mandos = [];
				this.loadingMandos = false;
			}
		});
	}

	abrirInformes() {
		const user = this.loginService.getUser();
		const groupInfo = {
			idGroup: this.group.idGroup,
			title: this.group.title,
			description: this.group.description,
			group: this.group,
			idUser: user?.idUser,
			assignedProjects: this.group.assignedProjects,
			assignedStudents: this.group.assignedStudents
		};
		localStorage.setItem('selectedGroupInfo', JSON.stringify(groupInfo));

		const modalRef = this.modalService.open(ReportingComponent, {
			scrollable: true,
			windowClass: `${MODAL_DIALOG_TYPES.W90} h-100`,
		});
		modalRef.componentInstance.idGrupo = this.group.idGroup;
		modalRef.componentInstance.group = this.group;
		modalRef.componentInstance.profileOverride = 'PROFESOR';
	}

	getEstadoLabel(estado: number): string {
		const labels: { [key: number]: string } = {
			0: 'Libre', 1: 'Asignado', 2: 'Perdido', 3: 'Estropeado'
		};
		return labels[estado] ?? 'Desconocido';
	}

	getEstadoClass(estado: number): string {
		const classes: { [key: number]: string } = {
			0: 'estado-libre', 1: 'estado-asignado', 2: 'estado-perdido', 3: 'estado-estropeado'
		};
		return classes[estado] ?? '';
	}

	get profesoresFiltrados(): any[] {
		const term = (this.searchProfesores || '').toLowerCase().trim();
		const filtered = !term ? [...this.profesores] : this.profesores.filter(p =>
			(p?.firstName ?? p?.name ?? '').toString().toLowerCase().includes(term) ||
			(p?.surname ?? p?.surName ?? '').toString().toLowerCase().includes(term) ||
			(p?.mail ?? p?.email ?? '').toString().toLowerCase().includes(term)
		);
		const col = this.profesoresSortColumn;
		if (!col) return filtered;
		const dir = this.profesoresSortDir === 'asc' ? 1 : -1;
		return filtered.sort((a, b) => {
			const sa = this.getProfesorField(a, col).toLowerCase();
			const sb = this.getProfesorField(b, col).toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	get mandosFiltrados(): any[] {
		const term = (this.searchMandos || '').toLowerCase().trim();
		const filtered = !term ? [...this.mandos] : this.mandos.filter(m =>
			(m?.idMando ?? '').toString().toLowerCase().includes(term) ||
			(m?.mac ?? '').toString().toLowerCase().includes(term) ||
			this.getEstadoLabel(m?.estado).toLowerCase().includes(term) ||
			(m?.battery ?? '').toString().toLowerCase().includes(term)
		);
		const col = this.mandosSortColumn;
		if (!col) return filtered;
		const dir = this.mandosSortDir === 'asc' ? 1 : -1;
		const numericCols: MandosSortColumn[] = ['idMando', 'battery'];
		return filtered.sort((a, b) => {
			if (col === 'estado') {
				const sa = this.getEstadoLabel(a?.estado).toLowerCase();
				const sb = this.getEstadoLabel(b?.estado).toLowerCase();
				if (sa < sb) return -1 * dir;
				if (sa > sb) return 1 * dir;
				return 0;
			}
			if (numericCols.includes(col)) {
				return (((+a?.[col]) || 0) - ((+b?.[col]) || 0)) * dir;
			}
			const sa = (a?.[col] ?? '').toString().toLowerCase();
			const sb = (b?.[col] ?? '').toString().toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	private getProfesorField(p: any, col: ProfesoresSortColumn): string {
		if (col === 'name') return (p?.firstName ?? p?.name ?? '').toString();
		if (col === 'surname') return (p?.surname ?? p?.surName ?? '').toString();
		if (col === 'email') return (p?.mail ?? p?.email ?? '').toString();
		return '';
	}

	sortProfesores(col: ProfesoresSortColumn) {
		if (this.profesoresSortColumn === col) {
			this.profesoresSortDir = this.profesoresSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.profesoresSortColumn = col;
			this.profesoresSortDir = 'asc';
		}
	}

	getProfesoresSortIcon(col: ProfesoresSortColumn): string {
		if (this.profesoresSortColumn !== col) return '↕';
		return this.profesoresSortDir === 'asc' ? '↑' : '↓';
	}

	sortMandos(col: MandosSortColumn) {
		if (this.mandosSortColumn === col) {
			this.mandosSortDir = this.mandosSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.mandosSortColumn = col;
			const numericCols: MandosSortColumn[] = ['idMando', 'battery'];
			this.mandosSortDir = numericCols.includes(col) ? 'desc' : 'asc';
		}
	}

	getMandosSortIcon(col: MandosSortColumn): string {
		if (this.mandosSortColumn !== col) return '↕';
		return this.mandosSortDir === 'asc' ? '↑' : '↓';
	}
}
